import { RotateCcw } from "lucide-react";
import { Toggle } from "@/components/shared";
import type { AccessPermission, ExtensionConsent } from "@/lib/services";

export const ACCESS_PERMISSIONS: readonly AccessPermission[] = ["read", "write"];

const LABELS: Record<AccessPermission, string> = { read: "Read", write: "Write" };

const DEFAULT_NOTES: Record<AccessPermission, string> = {
  read: "Allows AnthmAI to read and search data in this service.",
  write: "Allows AnthmAI to create, change or send things in this service.",
};

/** Notes for extensions where the generic wording undersells what the access means. */
const NOTES: Record<string, Partial<Record<AccessPermission, string>>> = {
  outlook: { read: "Allows AnthmAI to read and search your email." },
};

/** What turning *permission* on allows for the extension *slug*. */
export function accessNote(slug: string, permission: AccessPermission): string {
  return NOTES[slug]?.[permission] ?? DEFAULT_NOTES[permission];
}

interface ExtensionConsentRowProps {
  extension: ExtensionConsent;
  busyPermissions: ReadonlySet<AccessPermission>;
  onToggle: (slug: string, permission: AccessPermission, enabled: boolean) => void;
  onReset: (slug: string, permission: AccessPermission) => void;
}

/** One extension card with a switch for each permission the user can choose. */
export function ExtensionConsentRow({
  extension,
  busyPermissions,
  onToggle,
  onReset,
}: Readonly<ExtensionConsentRowProps>) {
  const titleId = `consent-${extension.slug}-title`;
  return (
    <fieldset
      aria-labelledby={titleId}
      className="min-w-0 p-4 bg-surface-primary border border-border-primary rounded-xl"
    >
      <h3 id={titleId} className="font-medium text-content-primary">
        {extension.name}
      </h3>
      {extension.description && <p className="text-sm text-content-tertiary mt-0.5">{extension.description}</p>}
      <div className="mt-3 space-y-3">
        {ACCESS_PERMISSIONS.filter((permission) => extension[permission].present).map((permission) => {
          const consent = extension[permission];
          const busy = busyPermissions.has(permission);
          return (
            <div key={permission}>
              <Toggle
                id={`consent-${extension.slug}-${permission}`}
                label={LABELS[permission]}
                checked={consent.effectiveEnabled}
                disabled={busy}
                onChange={(enabled) => onToggle(extension.slug, permission, enabled)}
              />
              <div className="flex items-center gap-2 mt-1">
                <p className="text-xs text-content-quaternary">{accessNote(extension.slug, permission)}</p>
                <span className="text-xs text-content-tertiary">
                  {consent.hasExplicitPreference ? "Custom" : "Default"}
                </span>
                {consent.hasExplicitPreference && (
                  <button
                    type="button"
                    onClick={() => onReset(extension.slug, permission)}
                    disabled={busy}
                    aria-label={`Reset ${extension.name} ${LABELS[permission]} to default`}
                    className="inline-flex items-center gap-1 text-xs text-content-tertiary hover:text-content-primary disabled:opacity-50"
                  >
                    <RotateCcw className="h-3 w-3" />
                    Reset
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
